/* eslint-disable jsx-a11y/accessible-emoji */
import React from 'react';
import classNames from 'classnames';
import { TARGETED_CARDS, ROLE_LABELS, EQUIP_SLOT_ORDER, EQUIP_SLOT_LABELS } from '../lib/game.js';
import './mobileGameArea.css';

// 卡牌中文名
const CARD_CN = {
    'Attack': '杀', 'Blaze': '火杀', 'Lightning Attack': '雷杀',
    'Duel': '决斗', 'Dismantle': '过河拆桥', 'Steal': '顺手牵羊',
    'Fire Attack': '火攻', 'Capture': '乐不思蜀', 'Starvation': '兵粮寸断', 'Lightning': '闪电',
    'Escape': '闪', 'Peach': '桃', 'Wine': '酒', 'Chains': '铁锁连环', 'Negate': '无懈可击',
    'Crossbow': '诸葛连弩', 'Fire Fan': '朱雀羽扇', 'Axe': '贯石斧', 'Longbow': '麒麟弓',
    'Green Dragon Blade': '青龙偃月刀', 'Serpent Spear': '丈八蛇矛', 'Ice Sword': '寒冰剑',
    'Ancient Scimitar': '古锭刀', 'Gender Swords': '雌雄双股剑', 'Black Pommel': '青釭剑',
    'Sky Scorcher': '方天画戟', 'Eight Trigrams': '八卦阵', 'Silver Helmet': '白银狮子',
    'Black Shield': '仁王盾', 'Wood Armor': '藤甲', 'Red Hare': '赤兔', 'Da Yuan': '大宛',
    'Zi Xing': '紫骍', 'Di Lu': '的卢', 'Storm Runner': '绝影', 'Shadow Runner': '爪黄飞电',
    'Hua Liu': '骅骝',
};
const IMAGE_MAX_SIZE = 512;
const EQUIP_CN = CARD_CN;

// 交互模式
const MODE_NONE = 'none';
const MODE_TARGET = 'target';   // 选了需要目标的牌，等点玩家
const MODE_DISMANTLE = 'dismantle';
const MODE_STEAL = 'steal';
const MODE_DISCARD = 'discard'; // 弃牌模式：点手牌直接弃入弃牌堆
const ACTION_LABEL = {
    [MODE_TARGET]: '选择目标玩家',
    [MODE_DISMANTLE]: '过河拆桥：点一名玩家拆其一张手牌',
    [MODE_STEAL]: '顺手牵羊：点一名玩家偷其一张手牌',
    [MODE_DISCARD]: '弃牌模式：点击手牌直接弃入弃牌堆（不结束回合）',
};

export default class MobileGameArea extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            mode: MODE_NONE,
            pendingIndex: undefined,     // 选中的手牌序号（等选目标）
            hpEditing: false,
            hpCur: '',
            hpMax: '',
            detailPlayer: undefined,     // 弹出查看的玩家详情
        };
    }

    componentDidUpdate(prevProps) {
        const { G, ctx, playerID } = this.props;
        // 弃牌阶段自动回到默认模式（手机同桌面）
        const prevStage = prevProps.ctx && prevProps.ctx.activePlayers && prevProps.ctx.activePlayers[playerID];
        const curStage = ctx && ctx.activePlayers && ctx.activePlayers[playerID];
        if (curStage === 'discard' && prevStage !== 'discard' && this.state.mode !== MODE_NONE) {
            this.setState({ mode: MODE_NONE, pendingIndex: undefined });
        }
        // 世界状态里某玩家已不存在（如对局被清）时关闭详情
        if (this.state.detailPlayer !== undefined && (!G.rolesDealt || !ctx.playOrder.includes(this.state.detailPlayer))) {
            this.setState({ detailPlayer: undefined });
        }
    }

    isGM() { return this.props.playerID === '-1'; }
    stage() {
        const { ctx, playerID } = this.props;
        const ap = ctx && ctx.activePlayers;
        return ap ? ap[playerID] : undefined;
    }
    // 是否处在“自由出牌/操作”的通用阶段（发牌后且未弃牌）
    canOperate() { return !this.isGM() && this.props.G.rolesDealt; }

    nameOf(pid) {
        const { matchData } = this.props;
        if (pid === '-1') return 'GM';
        if (pid === undefined) return '';
        // matchData 是 metadata.players 的对象数组（下标=座位号=playerID）
        if (matchData && matchData[pid] && matchData[pid].name) {
            return matchData[pid].name;
        }
        return `玩家 ${pid}`;
    }
    displayName(pid) {
        const { ctx } = this.props;
        if (this.isGM() || !ctx.playOrder) return this.nameOf(pid);
        return this.nameOf(pid);
    }
    currentName() { return this.displayName(this.props.ctx.currentPlayer); }

    readyState() {
        const { G, ctx, playerID } = this.props;
        if (!G.rolesDealt) {
            const map = G.ready || {};
            return { stage: 'lobby', ready: !!map[playerID] };
        }
        if (!G.kingRevealed && ctx.currentPlayer === playerID) {
            return { stage: 'reveal' };
        }
        const st = this.stage();
        if (st === 'play') return { stage: 'play', isTurn: ctx.currentPlayer === playerID };
        if (st === 'discard') {
            const hand = G.hands && G.hands[playerID] ? G.hands[playerID].length : 0;
            const limit = this.handLimit(playerID);
            return { stage: 'discard', need: Math.max(0, hand - limit), canEnd: hand <= limit };
        }
        return { stage: 'wait', isTurn: ctx.currentPlayer === playerID };
    }

    // ---- 动作 ----
    setMode(mode) { this.setState({ mode, pendingIndex: undefined }); }
    cancelMode() { this.setState({ mode: MODE_NONE, pendingIndex: undefined }); }

    selectCard(index) {
        const { G, moves, playerID } = this.props;
        const hand = G.hands[playerID] || [];
        const card = hand[index];
        if (!card) return;
        const st = this.stage();
        if (st === 'discard') { moves.discardCard(index); return; } // 旧对局兼容
        if (st !== 'play') return;
        if (this.state.mode === MODE_DISCARD) {
            // 弃牌模式：点手牌直接弃入弃牌堆（不结束回合、不触发使用/装备）
            moves.discardOne(index);
            return;
        }
        // 需目标的牌 → 进入目标模式
        if (TARGETED_CARDS.includes(card.type)) {
            this.setState({ mode: MODE_TARGET, pendingIndex: index });
        } else {
            moves.play(index); // 普通牌打出 / 装备牌进装备栏
        }
    }

    clickPlayer(pid) {
        const { G, moves, playerID } = this.props;
        if (this.isGM()) { this.setState({ detailPlayer: pid }); return; }
        if (!G.rolesDealt) return;
        const { mode, pendingIndex } = this.state;
        if (mode === MODE_TARGET && pendingIndex !== undefined) {
            moves.playTargeted(pendingIndex, pid);
            this.cancelMode();
        } else if (mode === MODE_DISMANTLE || mode === MODE_STEAL) {
            const hand = G.hands && G.hands[pid] ? G.hands[pid] : [];
            if (hand.length === 0) { alert('该玩家没有手牌'); return; }
            const move = mode === MODE_DISMANTLE ? moves.dismantle : moves.steal;
            move({ playerID: pid, index: Math.floor(Math.random() * hand.length) });
            this.cancelMode();
        } else if (pid !== playerID) {
            // 默认模式点其他玩家：查看明牌信息（含装备/判定区）
            this.setState({ detailPlayer: pid });
        }
    }

    pickUpDiscard(idx) { this.props.moves.pickUp(idx); }
    unequipSlot(slot) { this.props.moves.unequip(slot); }
    takeJudgmentCard(i) { this.props.moves.takeJudgment(i); }

    openHp() {
        const h = this.props.G.healths[this.props.playerID] || { current: 0, max: 1 };
        this.setState({ hpEditing: true, hpCur: String(h.current), hpMax: String(h.max) });
    }
    submitHp() {
        this.props.moves.setHealth({ current: this.state.hpCur, max: this.state.hpMax });
        this.setState({ hpEditing: false });
    }
    toggleTapped() { this.props.moves.toggleTapped(); }
    toggleFaceDown() { this.props.moves.toggleFaceDown(); }

    // ---- 派生状态 ----
    handLimit(pid) {
        const h = this.props.G.healths && this.props.G.healths[pid];
        if (!h || h.max <= 0) return 1;
        const ratio = h.current / h.max;
        let limit = ratio > 0.5 ? 4 : (ratio > 0.25 ? 2 : 1);
        if (this.props.G.roles && this.props.G.roles[pid] === 'King') limit += 1;
        return limit;
    }
    hpClass(pid) {
        const h = this.props.G.healths && this.props.G.healths[pid];
        if (!h || h.max <= 0) return 'danger';
        const r = h.current / h.max;
        return r > 0.5 ? 'ok' : (r > 0.25 ? 'warn' : 'danger');
    }
    avatarVisible(pid) {
        const { G, playerID } = this.props;
        if (!G.rolesDealt || !G.roles) return false;
        const role = G.roles[pid];
        if (!role || role === 'hidden') return false;
        if (playerID === '-1' || pid === playerID) return true;
        if (G.kingRevealed && role === 'King') return true;
        return false;
    }
    roleOf(pid) {
        const { G } = this.props;
        return G.roles ? G.roles[pid] : undefined;
    }
    canSeeHand(pid) { return this.isGM(); } // GM 明牌；普通玩家只见数量

    imgSrc(t) { return `./cards/${t}.jpg`; }

    /* ============ 渲染 ============ */

    // 手机端不加载玩家头像图片（省带宽与空间）：
    // 用“名字首字色块”表示玩家，横置/翻面/回合用状态符号标识
    renderPlayerAvatar(pid, size = 'tile') {
        const { G, playerID } = this.props;
        const tapped = G.tapped && G.tapped[pid];
        const facedown = G.facedown && G.facedown[pid];
        const isMe = pid === playerID && !this.isGM();
        const name = this.displayName(pid);
        const initial = (name && name.replace(/^玩家\s*/, '')[0]) || '?';
        const onClick = isMe && this.state.mode !== MODE_TARGET
            ? () => { this.fileInput && this.fileInput.click(); }
            : undefined;
        return <div
            className={classNames('mob-avatar', size, { me: isMe, cur: this.props.ctx.currentPlayer === pid })}
            onClick={onClick}
        >
            <span className='tile-letter'>{initial}</span>
            {this.avatarVisible(pid) && <span className={classNames('mob-role', this.roleOf(pid))}>{ROLE_LABELS[this.roleOf(pid)] || ''}</span>}
            {/* 横置/翻面状态用文字徽标（不依赖特殊字形，手机字体都支持） */}
            {tapped && <span className='st-tag tap'>{'横置'}</span>}
            {facedown && <span className='st-tag down'>{'翻面'}</span>}
            {isMe && <span className='upload-hint'>📷</span>}
        </div>;
    }

    renderOpponentCard(pid) {
        const { G, playerID } = this.props;
        const health = G.healths && G.healths[pid];
        const jg = G.judgment && G.judgment[pid] ? G.judgment[pid].length : 0;
        const eqCount = Object.values(G.equipment && G.equipment[pid] ? G.equipment[pid] : {}).filter(Boolean).length;
        const handN = G.hands && G.hands[pid] ? G.hands[pid].length : 0;
        const isTurn = this.props.ctx.currentPlayer === pid;
        return <div
            className={classNames('mob-opp', { isTurn, clickable: this.state.mode !== MODE_NONE || pid !== playerID })}
            onClick={() => this.clickPlayer(pid)}
        >
            {this.renderPlayerAvatar(pid, 'sm')}
            <div className='opp-body'>
                <div className='opp-name'>{this.displayName(pid)}</div>
                <div className='opp-hp'><span className={classNames('hp-dot', this.hpClass(pid))} />{health ? `${health.current}/${health.max}` : '--'} <span className='hint'>(上限{this.handLimit(pid)})</span></div>
                <div className='opp-badges'>
                    <span className='badge hand'>{`手牌 ${handN}`}</span>
                    {jg > 0 && <span className='badge jg'>{`⚖${jg}`}</span>}
                    {eqCount > 0 && <span className='badge eq'>{`🛡${eqCount}`}</span>}
                    {G.tapped && G.tapped[pid] && <span className='badge st'>横置</span>}
                    {G.facedown && G.facedown[pid] && <span className='badge st'>翻面</span>}
                </div>
            </div>
        </div>;
    }

    renderSelfBar() {
        const { G, playerID } = this.props;
        if (this.isGM()) return null;
        const health = G.healths && G.healths[playerID];
        const jg = G.judgment && G.judgment[playerID] ? G.judgment[playerID] : [];
        return <div className='mob-self'>
            {this.renderPlayerAvatar(playerID)}
            <div className='self-info'>
                <div className='self-name'>{this.displayName(playerID)}
                    {this.avatarVisible(playerID) && <span className={classNames('mob-role', this.roleOf(playerID))}>{ROLE_LABELS[this.roleOf(playerID)] || ''}</span>}
                </div>
                <div className='self-hp' onClick={() => this.openHp()} title='点击修改 HP'>
                    <span className={classNames('hp-dot', this.hpClass(playerID))} />
                    {health ? `${health.current}/${health.max}` : '--'}
                    <span className='edit-hint'>✎ HP（点按编辑）</span>
                </div>
                <div className='self-eq'>
                    {EQUIP_SLOT_ORDER.map(slot => {
                        const card = G.equipment && G.equipment[playerID] && G.equipment[playerID][slot];
                        return <div key={slot} className={classNames('eq-slot', { filled: !!card })}
                            onClick={card ? () => { if (window.confirm(`卸下装备「${EQUIP_CN[card.type] || card.type}」？`)) this.unequipSlot(slot); } : undefined}
                        >
                            {card ? <><img src={this.imgSrc(card.type)} alt="" /><em>{EQUIP_CN[card.type] || ''}</em></> : EQUIP_SLOT_LABELS[slot]}
                        </div>;
                    })}
                </div>
                {jg.length > 0 && <div className='self-jg'>
                    {jg.map((c, i) => <div key={c.id} className='jg-chip' onClick={() => this.takeJudgmentCard(i)} title='点击弃入弃牌堆'>
                        <img src={this.imgSrc(c.type)} alt="" />{CARD_CN[c.type] || c.type}</div>)}
                    <span className='jg-hint'>判定区：点牌弃掉</span>
                </div>}
            </div>
            <div className='self-actions'>
                <button className={classNames('sbtn', { on: G.tapped && G.tapped[playerID] })} onClick={() => this.toggleTapped()}>{G.tapped && G.tapped[playerID] ? '↩ 竖回' : '↻ 横置'}</button>
                <button className={classNames('sbtn', { on: G.facedown && G.facedown[playerID] })} onClick={() => this.toggleFaceDown()}>{G.facedown && G.facedown[playerID] ? '🃏 翻回' : '🎴 翻面'}</button>
            </div>
        </div>;
    }

    renderCenter() {
        const { G, moves } = this.props;
        const { deck, discard, rolesDealt, lastJudged } = G;
        const canDraw = rolesDealt && this.stage() === 'play';
        const canPick = rolesDealt && this.stage() === 'play';
        const top = discard.length ? discard[discard.length - 1] : undefined;
        return <div className='mob-center'>
            <div className='stack deck' onClick={canDraw ? () => moves.draw() : undefined}>
                <div className='pile'>
                    <img src="./cards/Card Back.jpg" alt="" draggable={false} />
                    <span className='cnt'>{deck.length}</span>
                </div>
                <div className='lbl'>{canDraw ? '点牌堆摸牌' : '牌堆'}</div>
            </div>
            <div className='mid-judge'>
                <button className='jbtn' disabled={!canDraw} onClick={() => moves.judge()}>⚖ 判定</button>
                {lastJudged && lastJudged.card && <div className='judge-res'>{`判定：${CARD_CN[lastJudged.card.type] || lastJudged.card.type}`}</div>}
            </div>
            {/* 弃牌堆：单张顶牌清晰大图 + 中文牌名（不再是半透明叠加） */}
            <div className={classNames('stack discard', { empty: !top })} onClick={canPick && top ? () => this.pickUpDiscard(discard.length - 1) : undefined}>
                <div className='pile'>
                    {top
                        ? <><img className='topface' src={this.imgSrc(top.type)} alt={CARD_CN[top.type] || top.type} draggable={false} />
                            <span className='discard-name'>{CARD_CN[top.type] || top.type}</span></>
                        : <div className='empty'>—</div>}
                    <span className='cnt'>{discard.length}</span>
                </div>
                <div className='lbl'>{canPick && top ? '点此捡回顶牌' : '弃牌堆'}</div>
            </div>
        </div>;
    }

    renderHand() {
        const { G, playerID } = this.props;
        if (this.isGM() || !G.rolesDealt) return null;
        const hand = G.hands && G.hands[playerID] ? G.hands[playerID] : [];
        const st = this.stage();
        const clickable = st === 'play' || st === 'discard';
        const discardMode = this.state.mode === MODE_DISCARD;
        return <div className='mob-hand'>
            <div className='hand-title'>
                {st === 'discard'
                    ? '弃牌阶段：点牌弃掉（旧对局兼容）'
                    : (discardMode ? '弃牌模式：点牌直接弃入弃牌堆（不结束回合）' : '我的手牌：点牌操作')}
            </div>
            <div className='hand-scroll'>
                {hand.length === 0 && <div className='no-hand'>（无手牌）</div>}
                {hand.map((card, i) => {
                    const chosen = this.state.mode === MODE_TARGET && this.state.pendingIndex === i;
                    return <div key={card.id} className={classNames('hcard', { chosen })}
                        onClick={clickable ? () => this.selectCard(i) : undefined}>
                        <img src={this.imgSrc(card.type)} alt="" draggable={false} />
                        <em>{CARD_CN[card.type] || card.type}</em>
                    </div>;
                })}
            </div>
        </div>;
    }

    renderModeBar() {
        const { G } = this.props;
        if (this.isGM() || !G.rolesDealt) return null;
        const st = this.stage();
        const rs = this.readyState();
        const cur = this.state.mode;
        return <div className='mob-mode'>
            {[MODE_NONE, MODE_DISMANTLE, MODE_STEAL, MODE_DISCARD].map(m => (
                <button key={m}
                    className={classNames('mbtn', { on: cur === m })}
                    onClick={() => this.setMode(cur === m ? MODE_NONE : m)}>
                    {m === MODE_NONE ? '默认' : m === MODE_DISMANTLE ? '过河拆桥' : m === MODE_STEAL ? '顺手牵羊' : '弃牌'}
                </button>
            ))}
            {cur === MODE_TARGET && <span className='mode-tip'>选目标：点上方玩家</span>}
            {cur !== MODE_NONE && cur !== MODE_TARGET && <span className='mode-tip'>{ACTION_LABEL[cur]}</span>}
            {cur !== MODE_NONE && <button className='mbtn x' onClick={() => this.cancelMode()}>✕ 取消</button>}
            {st === 'discard' && rs.canEnd && <button className='mbtn gold' onClick={() => this.props.moves.finishDiscard()}>结束回合</button>}
            {st === 'play' && rs.isTurn && G.kingRevealed && <button className='mbtn gold' onClick={() => this.props.moves.endPlay()}>结束出牌</button>}
        </div>;
    }

    renderPreGame() {
        const { G, ctx, playerID, moves } = this.props;
        if (this.isGM()) return null;
        if (!G.rolesDealt) {
            const ready = G.ready && G.ready[playerID];
            return <div className='mob-pregame'>
                <button className={classNames('big-ready', { on: ready })} onClick={() => moves.ready()}>
                    {ready ? '✓ 已就位（点我取消）' : '点我就位'}
                </button>
                <p>全员就位后由 GM 发放身份牌</p>
            </div>;
        }
        if (!G.kingRevealed && ctx.currentPlayer === playerID) {
            return <div className='mob-pregame'>
                <button className='big-ready gold' onClick={() => moves.revealKingAndHandTurn()}>
                    ⭐ 将回合交给主公并展示身份
                </button>
            </div>;
        }
        return null;
    }

    renderTopBar() {
        const { G } = this.props;
        const leave = typeof window !== 'undefined' && window.__leaveSanguoGame
            ? () => window.__leaveSanguoGame()
            : undefined;
        if (this.isGM()) {
            return <div className='mob-topbar gm'>
                <div className='top-title'>🕵️ GM 旁观模式 · 全员手牌可见</div>
                <div className='top-sub'>{G.rolesDealt ? `当前回合：${this.currentName()}` : '等待发身份牌…'}</div>
                {leave && <button className='top-leave' onClick={leave}>🏠 返回大厅</button>}
            </div>;
        }
        let text;
        if (!G.rolesDealt) text = '等待就位 / 发身份牌';
        else if (!G.kingRevealed) text = `当前回合：${this.currentName()}（未展示主公）`;
        else if (this.stage() === 'discard') text = '弃牌阶段';
        else text = `当前回合：${this.currentName()}`;
        return <div className='mob-topbar'>
            <div className='top-title'>跑团卡牌桌</div>
            <div className='top-sub'>{text}</div>
            {leave && <button className='top-leave' onClick={leave}>🏠 返回大厅</button>}
        </div>;
    }

    /* ---- GM 视图：逐人展示手牌（明牌）与状态 ---- */
    renderGMPlayerRow(pid) {
        const { G } = this.props;
        const health = G.healths && G.healths[pid];
        const hand = G.hands && G.hands[pid] ? G.hands[pid] : [];
        const jg = G.judgment && G.judgment[pid] ? G.judgment[pid] : [];
        const eq = G.equipment && G.equipment[pid] ? G.equipment[pid] : {};
        const role = this.roleOf(pid);
        return <div key={pid} className='gm-row'>
            <div className='gm-head'>
                {this.renderPlayerAvatar(pid, 'sm')}
                <div className='gm-meta'>
                    <div className='gm-name'>{this.displayName(pid)}
                        {role && role !== 'hidden' ? <span className={classNames('mob-role', role)}>{ROLE_LABELS[role] || role}</span> : null}
                        {G.ctx && G.ctx.currentPlayer === pid && <span className='turn-tag'>🎯 回合</span>}
                    </div>
                    <div className='gm-hp'>HP {health ? `${health.current}/${health.max}` : '--'} · 上限 {this.handLimit(pid)} · 手牌 {hand.length} 张</div>
                    <div className='gm-badges'>
                        {G.tapped && G.tapped[pid] && <span className='badge st'>横置</span>}
                        {G.facedown && G.facedown[pid] && <span className='badge st'>翻面</span>}
                        {eq && Object.values(eq).filter(Boolean).map(c => c.type).length > 0 &&
                            <span className='badge eq'>{`装备 ${Object.values(eq).filter(Boolean).length}`}</span>}
                        {jg.length > 0 && <span className='badge jg'>{`判定区 ${jg.length}`}</span>}
                    </div>
                </div>
            </div>
            {hand.length > 0 && <div className='gm-hand'>
                {hand.map(c => <div key={c.id} className='hcard sm'><img src={this.imgSrc(c.type)} alt="" /><em>{CARD_CN[c.type] || c.type}</em></div>)}
            </div>}
            {jg.length > 0 && <div className='gm-jg'>
                {jg.map(c => <span key={c.id} className='jg-chip'><img src={this.imgSrc(c.type)} alt="" />{CARD_CN[c.type] || c.type}</span>)}
            </div>}
        </div>;
    }

    renderGMActions() {
        const { G, ctx, moves, playerID } = this.props;
        if (!this.isGM()) return null;
        const readyMap = G.ready || {};
        const readyCount = ctx.playOrder.filter(p => readyMap[p] === true).length;
        const allReady = readyCount === ctx.playOrder.length;
        return <div className='gm-actions-m'>
            <div className='gm-status-m'>
                {G.rolesDealt ? '身份已发放' : `就位 ${readyCount}/${ctx.playOrder.length}${allReady ? '，可发身份牌' : ''}`}
            </div>
            {allReady && !G.rolesDealt && <button className='mbtn gold' onClick={() => moves.dealRoles()}>发身份牌</button>}
            <button className='mbtn red' onClick={() => {
                if (window.confirm('确认清空房间、重新开局？（保留玩家图片与体力上限）')) moves.gmReset();
            }}>清空房间</button>
            <button className='mbtn' onClick={async () => {
                if (window.confirm('确认清空所有玩家座位？')) {
                    try { await fetch(`/api/gm/clear-players?matchID=${encodeURIComponent(this.props.matchID)}`, { method: 'POST' }); } catch (e) { }
                    moves.gmReset();
                }
            }}>清空玩家</button>
            {playerID !== undefined && <span className='gm-me'>我是 GM（ID {playerID}）</span>}
        </div>;
    }

    renderPlayerDetail() {
        const { G, playerID } = this.props;
        const pid = this.state.detailPlayer;
        if (pid === undefined) return null;
        const health = G.healths && G.healths[pid];
        const eq = G.equipment && G.equipment[pid] ? G.equipment[pid] : {};
        const jg = G.judgment && G.judgment[pid] ? G.judgment[pid] : [];
        const isMe = pid === playerID;
        return <div className='overlay' onClick={() => this.setState({ detailPlayer: undefined })}>
            <div className='detail-panel' onClick={e => e.stopPropagation()}>
                <div className='detail-head'>
                    {this.renderPlayerAvatar(pid, 'lg')}
                    <div>
                        <div className='detail-name'>{this.displayName(pid)}
                            {this.avatarVisible(pid) && this.roleOf(pid) && <span className={classNames('mob-role', this.roleOf(pid))}>{ROLE_LABELS[this.roleOf(pid)]}</span>}
                        </div>
                        <div>HP {health ? `${health.current}/${health.max}` : '--'} · 手牌上限 {this.handLimit(pid)}</div>
                    </div>
                </div>
                <div className='detail-eq'>
                    {EQUIP_SLOT_ORDER.map(slot => {
                        const card = eq[slot];
                        return <div key={slot} className={classNames('eq-slot lg', { filled: !!card })}>
                            {card ? <><img src={this.imgSrc(card.type)} alt="" /><em>{EQUIP_CN[card.type] || ''}</em></> : EQUIP_SLOT_LABELS[slot]}
                        </div>;
                    })}
                </div>
                <div className='detail-jg'>
                    <b>判定区：</b>
                    {jg.length === 0 ? '无' : jg.map(c => <span key={c.id} className='jg-chip'><img src={this.imgSrc(c.type)} alt="" />{CARD_CN[c.type] || c.type}</span>)}
                    {isMe && jg.length > 0 && <button className='mbtn x sm' onClick={() => this.takeJudgmentCard(jg.length - 1)}>弃掉顶牌</button>}
                </div>
                <div className='detail-foot'>
                    <button className='mbtn' onClick={() => this.setState({ detailPlayer: undefined })}>关闭</button>
                    {!this.isGM() && G.rolesDealt && pid !== playerID &&
                        <button className='mbtn gold' onClick={() => { this.setState({ detailPlayer: undefined }); this.clickPlayer(pid); }}>
                            {this.state.mode === MODE_TARGET ? '作为目标' : '查看手牌信息'}</button>}
                </div>
            </div>
        </div>;
    }

    renderHpOverlay() {
        if (!this.state.hpEditing) return null;
        return <div className='overlay' onClick={() => this.setState({ hpEditing: false })}>
            <div className='hp-panel' onClick={e => e.stopPropagation()}>
                <div className='hp-title'>设置 HP</div>
                <div className='hp-fields'>
                    <label>当前 HP<input type="number" value={this.state.hpCur} onChange={e => this.setState({ hpCur: e.target.value })} /></label>
                    <label>上限<input type="number" value={this.state.hpMax} onChange={e => this.setState({ hpMax: e.target.value })} /></label>
                </div>
                <div className='hp-btns'>
                    <button className='mbtn gold' onClick={() => this.submitHp()}>确定</button>
                    <button className='mbtn' onClick={() => this.setState({ hpEditing: false })}>取消</button>
                </div>
            </div>
        </div>;
    }

    renderUploadInput() {
        return <input type="file" accept="image/*" ref={el => this.fileInput = el} style={{ display: 'none' }} onChange={this.handleFile} />;
    }

    handleFile = e => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => {
            const image = new Image();
            image.onload = async () => {
                let dataUrl;
                try {
                    const scale = Math.min(1, IMAGE_MAX_SIZE / Math.max(image.width, image.height));
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.max(1, Math.round(image.width * scale));
                    canvas.height = Math.max(1, Math.round(image.height * scale));
                    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
                    dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                } catch (err) {
                    window.alert('图片处理失败：' + (err && err.message ? err.message : '未知错误'));
                    return;
                }
                let blob;
                try {
                    const [head, body] = dataUrl.split(',');
                    const mime = head.match(/:(.*?);/)[1];
                    const bin = atob(body);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    blob = new Blob([u8], { type: mime });
                } catch (err) { blob = undefined; }
                try {
                    const form = new FormData();
                    form.append('image', blob, 'avatar.jpg');
                    form.append('matchID', this.props.matchID || '');
                    form.append('playerID', this.props.playerID || '');
                    const res = await fetch('/api/upload', { method: 'POST', body: form });
                    const data = await res.json();
                    if (res.ok && data.url) this.props.moves.setImage(data.url);
                    else throw new Error(data.error || '上传失败');
                } catch (err) {
                    try {
                        this.props.moves.setImage(dataUrl);
                        window.alert('上传服务器失败，已改用本地图片');
                    } catch (e2) {
                        window.alert('图片上传失败：' + (err && err.message ? err.message : '未知错误'));
                    }
                }
            };
            image.onerror = () => window.alert('无法读取该图片文件');
            image.src = ev.target.result;
        };
        reader.onerror = () => window.alert('无法读取该图片文件');
        reader.readAsDataURL(file);
    }

    // 行动横幅：显示最近一次“谁用【什么牌】指向/打了谁”（全桌可见，含 GM）
    renderActionBanner() {
        const { G } = this.props;
        const t = G.targets && G.targets.length > 0 ? G.targets[G.targets.length - 1] : undefined;
        if (!t || !G.rolesDealt) {
            return undefined;
        }
        const src = this.displayName(t.source);
        const dst = t.target === this.props.playerID && !this.isGM() ? '我' : this.displayName(t.target);
        const name = CARD_CN[t.cardType] || t.cardType;
        return <div className='mob-action' key={`act-${t.id}`}>
            <span className='act-src'>{src}</span>
            <span className='act-arrow'>➜</span>
            <span className='act-card'>{name}</span>
            <span className='act-arrow'>➜</span>
            <span className='act-dst'>{dst}</span>
        </div>;
    }

    render() {
        const { ctx, playerID } = this.props;
        if (!ctx || !ctx.playOrder) return null;
        const isGM = this.isGM();
        const { G } = this.props;

        let body;
        if (isGM) {
            body = <div className='gm-body'>
                {this.renderGMActions()}
                {this.renderActionBanner()}
                {ctx.playOrder.map(pid => this.renderGMPlayerRow(pid))}
            </div>;
        } else {
            const showOpps = ctx.playOrder && ctx.playOrder.length > 0;
            const opps = ctx.playOrder.filter(p => p !== playerID);
            body = <div className='ply-body'>
                {this.renderPreGame()}
                {showOpps && <div className='opps-grid'>{opps.map(pid => this.renderOpponentCard(pid))}</div>}
                {this.renderActionBanner()}
                {G.rolesDealt && this.renderCenter()}
                {G.rolesDealt && this.renderSelfBar()}
            </div>;
        }

        return <div className={classNames('mob-root', { gm: isGM })}>
            {this.renderTopBar()}
            <div className='mob-scroll'>{body}</div>
            {this.renderModeBar()}
            {this.renderHand()}
            {this.renderPlayerDetail()}
            {this.renderHpOverlay()}
            {this.renderUploadInput()}
        </div>;
    }
}
